import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  AccountTree,
  Business,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

interface HierarchyNode {
  id: string;
  label: string;
  type: 'fund' | 'administrator' | 'custodian' | 'auditor' | 'investment_manager';
  level: number;
  children: HierarchyNode[];
  expanded: boolean;
  visible: boolean;
}

interface FundHierarchyVisualizationProps {
  fundStructures: any[];
  selectedFund?: any;
  onNodeClick?: (node: HierarchyNode) => void;
}

const FundHierarchyVisualization: React.FC<FundHierarchyVisualizationProps> = ({
  fundStructures,
  selectedFund,
  onNodeClick,
}) => {
  const [hierarchyData, setHierarchyData] = useState<HierarchyNode[]>([]);
  const [viewMode, setViewMode] = useState<'all' | 'selected'>('all');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Generate hierarchy data from fund structures
  useEffect(() => {
    if (!fundStructures || fundStructures.length === 0) {
      setHierarchyData([]);
      return;
    }

    const buildHierarchy = (structures: any[]): HierarchyNode[] => {
      return structures.map((structure) => {
        const fund = structure.fund;
        
        // Group service providers by type
        const providersByType = structure.serviceProviders.reduce((acc: any, provider: any) => {
          if (!acc[provider.provider_type]) {
            acc[provider.provider_type] = [];
          }
          acc[provider.provider_type].push(provider);
          return acc;
        }, {});

        // Create fund node
        const fundNode: HierarchyNode = {
          id: fund.id,
          label: fund.name,
          type: 'fund',
          level: 0,
          expanded: true,
          visible: true,
          children: Object.entries(providersByType).map(([type, providers]: [string, any]) => ({
            id: `${fund.id}-${type}`,
            label: `${type.charAt(0).toUpperCase() + type.slice(1)}s`,
            type: type as any,
            level: 1,
            expanded: true,
            visible: true,
            children: providers.map((provider: any) => ({
              id: provider.id,
              label: provider.name,
              type: provider.provider_type,
              level: 2,
              expanded: false,
              visible: true,
              children: [],
            })),
          })),
        };

        return fundNode;
      });
    };

    const hierarchy = buildHierarchy(fundStructures);
    setHierarchyData(hierarchy);
    
    // Auto-expand all nodes initially
    const allNodeIds = new Set<string>();
    const collectNodeIds = (nodes: HierarchyNode[]) => {
      nodes.forEach(node => {
        allNodeIds.add(node.id);
        collectNodeIds(node.children);
      });
    };
    collectNodeIds(hierarchy);
    setExpandedNodes(allNodeIds);
  }, [fundStructures]);

  // Filter data based on view mode
  const filteredData = React.useMemo(() => {
    if (viewMode === 'selected' && selectedFund) {
      return hierarchyData.filter(node => node.id === selectedFund.id);
    }
    return hierarchyData;
  }, [hierarchyData, viewMode, selectedFund]);

  const toggleNodeExpansion = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const getNodeColor = (type: string) => {
    switch (type) {
      case 'fund': return '#1976d2';
      case 'administrator': return '#388e3c';
      case 'custodian': return '#f57c00';
      case 'auditor': return '#7b1fa2';
      case 'investment_manager': return '#d32f2f';
      default: return '#666';
    }
  };

  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'fund': return <AccountTree />;
      default: return <Business />;
    }
  };

  const renderNode = (node: HierarchyNode, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;

    return (
      <Box key={node.id} sx={{ ml: depth * 2 }}>
        <Box
          display="flex"
          alignItems="center"
          sx={{
            py: 1,
            px: 1,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            backgroundColor: node.type === 'fund' ? 'primary.light' : 'transparent',
            color: node.type === 'fund' ? 'primary.contrastText' : 'text.primary',
          }}
          onClick={() => {
            if (hasChildren) {
              toggleNodeExpansion(node.id);
            }
            onNodeClick?.(node);
          }}
        >
          {hasChildren && (
            <IconButton size="small" sx={{ mr: 1, color: 'inherit' }}>
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
          
          {!hasChildren && <Box sx={{ width: 32 }} />}
          
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: getNodeColor(node.type),
              mr: 1,
            }}
          />
          
          <Box sx={{ mr: 1 }}>
            {getNodeIcon(node.type)}
          </Box>
          
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {node.label}
          </Typography>
          
          <Chip
            label={node.type}
            size="small"
            sx={{
              backgroundColor: getNodeColor(node.type),
              color: 'white',
              fontSize: '0.7rem',
            }}
          />
        </Box>

        {isExpanded && hasChildren && (
          <Box>
            {node.children.map(child => renderNode(child, depth + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const getTotalNodes = (nodes: HierarchyNode[]): number => {
    return nodes.reduce((total, node) => {
      return total + 1 + getTotalNodes(node.children);
    }, 0);
  };

  const getTotalFunds = (nodes: HierarchyNode[]): number => {
    return nodes.filter(node => node.type === 'fund').length;
  };

  const getTotalProviders = (nodes: HierarchyNode[]): number => {
    return nodes.reduce((total, node) => {
      if (node.type !== 'fund') {
        return total + 1 + getTotalProviders(node.children);
      }
      return total + getTotalProviders(node.children);
    }, 0);
  };

  return (
    <Box>
      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>View Mode</InputLabel>
          <Select
            value={viewMode}
            label="View Mode"
            onChange={(e) => setViewMode(e.target.value as any)}
          >
            <MenuItem value="all">All Funds</MenuItem>
            <MenuItem value="selected">Selected Fund</MenuItem>
          </Select>
        </FormControl>

        <Box display="flex" gap={1}>
          <Tooltip title="Expand All">
            <IconButton
              size="small"
              onClick={() => {
                const allNodeIds = new Set<string>();
                const collectNodeIds = (nodes: HierarchyNode[]) => {
                  nodes.forEach(node => {
                    allNodeIds.add(node.id);
                    collectNodeIds(node.children);
                  });
                };
                collectNodeIds(filteredData);
                setExpandedNodes(allNodeIds);
              }}
            >
              <ExpandMore />
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse All">
            <IconButton
              size="small"
              onClick={() => setExpandedNodes(new Set())}
            >
              <ExpandLess />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        <Chip icon={<AccountTree />} label="Funds" color="primary" size="small" />
        <Chip icon={<Business />} label="Administrators" color="success" size="small" />
        <Chip icon={<Business />} label="Custodians" color="warning" size="small" />
        <Chip icon={<Business />} label="Auditors" color="secondary" size="small" />
        <Chip icon={<Business />} label="Investment Managers" color="error" size="small" />
      </Box>

      {/* Hierarchy Tree */}
      <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Box p={2}>
          {filteredData.length === 0 ? (
            <Box textAlign="center" py={4}>
              <AccountTree sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Hierarchy Data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {viewMode === 'selected' 
                  ? 'Select a fund to view its hierarchy'
                  : 'No fund structures available to visualize'
                }
              </Typography>
            </Box>
          ) : (
            <Box>
              {filteredData.map(node => renderNode(node))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Statistics */}
      <Box mt={2} display="flex" gap={2} flexWrap="wrap">
        <Chip label={`${getTotalFunds(filteredData)} Funds`} variant="outlined" />
        <Chip label={`${getTotalProviders(filteredData)} Service Providers`} variant="outlined" />
        <Chip label={`${getTotalNodes(filteredData)} Total Nodes`} variant="outlined" />
      </Box>
    </Box>
  );
};

export default FundHierarchyVisualization;
